import React, { useState, useEffect } from 'react';
import axiosInstance from '../../axiosConfig';
import { FaImage, FaVideo, FaLink, FaTimes } from 'react-icons/fa';
import {FaArrowUpFromBracket} from "react-icons/fa6";
import './CreatePost.css';
import './NewPostModal.css';
import './UnifiedFeed.css';

const CreatePost = ({ apiEndpoint, onPostCreated, courseId }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [imageFiles, setImageFiles] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [uploadType, setUploadType] = useState('images');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showForm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showForm]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showForm) {
        resetForm();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showForm]);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);

    const validFiles = [];
    let hasError = false;

    files.forEach(file => {
      if (file.size > 50 * 1024 * 1024) {
        setErrorMessage('One or more files exceed the 50MB limit');
        hasError = true;
        return;
      }
      if (!file.type.startsWith('image/')) {
        setErrorMessage('Only image files are allowed');
        hasError = true;
        return;
      }
      validFiles.push(file);
    });

    if (!hasError) {
      setImageFiles(prevFiles => [...prevFiles, ...validFiles]);
      setVideoFile(null);
      setErrorMessage('');
    }
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        setErrorMessage('Video file is too large. Maximum size is 50MB.');
        return;
      }

      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        if (video.duration > 30) {
          setErrorMessage('Video is too long. Maximum duration is 30 seconds.');
          e.target.value = '';
          return;
        }
        setVideoFile(file);
        setImageFiles([]);
        setErrorMessage('');
      };
      video.src = URL.createObjectURL(file);
    }
  };

  const removeImage = (index) => {
    setImageFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const removeVideo = () => {
    setVideoFile(null);
  };

  const validateUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    setUploadProgress(0);

    if (imageFiles.length > 0 && videoFile) {
      setErrorMessage('You can upload either images or a video, not both.');
      setIsSubmitting(false);
      return;
    }

    if (linkUrl && !validateUrl(linkUrl)) {
      setErrorMessage('Please enter a valid URL (including http:// or https://)');
      setIsSubmitting(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('content', content);

      if (courseId) {
        formData.append('course', courseId);
      }

      if (linkUrl) {
        formData.append('link_url', linkUrl);
      }

      if (uploadType === 'images' && imageFiles.length > 0) {
        imageFiles.forEach((file, index) => {
          formData.append('images', file, file.name);
        });
      } else if (uploadType === 'video' && videoFile) {
        formData.append('video', videoFile);
      }

      const response = await axiosInstance.post(apiEndpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      setTitle('');
      setContent('');
      setLinkUrl('');
      setImageFiles([]);
      setVideoFile(null);
      setShowForm(false);
      setShowLinkInput(false);
      setUploadProgress(0);

      onPostCreated(response.data);
    } catch (error) {
      console.error('Error creating post:', error);
      if (error.response?.data?.video) {
        setErrorMessage(`Video error: ${error.response.data.video}`);
      } else if (error.response?.data?.error) {
        setErrorMessage(`Error: ${error.response.data.error}`);
      } else {
        setErrorMessage('Failed to create post. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const resetForm = () => {
    setTitle('');
    setContent('');
    setLinkUrl('');
    setImageFiles([]);
    setVideoFile(null);
    setShowForm(false);
    setShowLinkInput(false);
    setErrorMessage('');
    setUploadProgress(0);
  };

  // Close modal when clicking outside
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      resetForm();
    }
  };

  return (
    <div className="create-post-container">
      <button
        className="uf-create-btn"
        onClick={() => setShowForm(!showForm)}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        New Post
      </button>

      {showForm && (
        <div className="npm-overlay" onClick={handleOverlayClick}>
          <div className="npm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="npm-header">
              <h2 className="npm-title">New Post</h2>
              <button className="npm-close" onClick={resetForm}>
                <FaTimes />
              </button>
            </div>

            <div className="form-body">
              {errorMessage && <p className="error-message">{errorMessage}</p>}

              <div className="form-group">
                <input
                  type="text"
                  placeholder="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <textarea
                  placeholder="Write your post here..."
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows="5"
                  required
                  className="form-textarea"
                />
              </div>

              {showLinkInput && (
                <div className="form-group">
                  <div className="link-input-container">
                    <input
                      type="url"
                      placeholder="https://example.com"
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                      className="form-input link-input"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setShowLinkInput(false);
                        setLinkUrl('');
                      }}
                      className="remove-link-button"
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>
              )}

              <div className="media-controls">
                <div className="upload-type-selector">
                  <button
                    type="button"
                    className={`media-button-small ${uploadType === 'images' ? 'active' : ''}`}
                    onClick={() => {
                      setUploadType('images');
                      setVideoFile(null);
                    }}
                    title="Images"
                  >
                    <FaImage />
                  </button>
                  <button
                    type="button"
                    className={`media-button-small ${uploadType === 'video' ? 'active' : ''}`}
                    onClick={() => {
                      setUploadType('video');
                      setImageFiles([]);
                    }}
                    title="Video"
                  >
                    <FaVideo />
                  </button>
                </div>

                <div className="media-buttons">
                  {uploadType === 'images' ? (
                    <label className="media-button-small" title="Add Images">
                      <FaArrowUpFromBracket />
                      <input
                        type="file"
                        accept="image/*,.heic,.heif"
                        multiple
                        onChange={handleImageUpload}
                        className="visually-hidden"
                      />
                    </label>
                  ) : (
                    <label className="media-button-small" title="Add Video">
                      <FaArrowUpFromBracket />
                      <input
                        type="file"
                        accept="video/*"
                        onChange={handleVideoUpload}
                        className="visually-hidden"
                      />
                    </label>
                  )}

                  {!showLinkInput && (
                    <button
                      type="button"
                      onClick={() => setShowLinkInput(true)}
                      className="media-button-small"
                      title="Add Link"
                    >
                      <FaLink />
                    </button>
                  )}
                </div>
              </div>

              {uploadType === 'video' && (
                <div className="video-help-text">
                  Maximum: 20 seconds duration, 50MB file size
                </div>
              )}

              <div className="media-previews">
                {uploadType === 'images' && imageFiles.length > 0 && (
                  <div className="image-previews">
                    <h4>Selected Images:</h4>
                    <div className="nested-image-previews">
                      {imageFiles.map((file, index) => (
                        <div key={index} className="nested-image-preview">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Preview ${index + 1}`}
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            aria-label="Remove image"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {uploadType === 'video' && videoFile && (
                  <div className="video-preview">
                    <h4>Selected Video:</h4>
                    <video
                      src={URL.createObjectURL(videoFile)}
                      controls
                    />
                    <div style={{ marginTop: '10px' }}>
                      <button
                        type="button"
                        onClick={removeVideo}
                        className="remove-video-button"
                      >
                        <FaTimes /> Remove Video
                      </button>
                      <span className="video-filename">
                        {videoFile.name}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <span className="progress-text">
                    Uploading... {uploadProgress}%
                  </span>
                </div>
              )}

              {linkUrl && (
                <div className="link-preview">
                  <h4>Link:</h4>
                  <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="preview-link">
                    {linkUrl}
                  </a>
                </div>
              )}
            </div>

            <div className="npm-footer">
              <button
                type="button"
                onClick={resetForm}
                className="npm-cancel"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="npm-submit"
              >
                {isSubmitting ? 'Posting...' : 'Post'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatePost;